import { router } from 'expo-router';
import { Fragment, forwardRef, useImperativeHandle, useRef } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import {
  Avatar,
  Button,
  Center,
  HStack,
  Text,
  VStack,
  theme,
} from '@squire/ui';

import {
  PencilIcon,
  ScissorIcon,
  TrashIcon,
  UserIcon,
} from '@/components/_icons';
import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { BottomSheet } from '@/components/_shared/BottomSheet';
import { Card } from '@/components/_shared/Card';
import { routes } from '@/constants/routes';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { useWidget } from '@/providers/WidgetProvider';
import type { CartItem } from '@/types/booking';
import {
  DateFormat,
  formatDate,
  formatDuration,
  parseDateWithTimezone,
} from '@/utils/dateUtils';
import { formatPrice } from '@/utils/internationalizationUtils';

import { styles } from './styles';
import type { EditOrderBottomSheetProps } from './types';

const EditOrderBottomSheet = forwardRef<
  BottomSheetRef,
  EditOrderBottomSheetProps
>(
  (
    {
      cart,
      shop,
      onEditLocation,
      onEditProfessional,
      onEditService,
      onEditTime,
      onRemoveAddon,
      onDone,
      onDismiss,
      onCloseOrder,
    },
    ref,
  ) => {
    const bottomSheetRef = useRef<BottomSheetRef>(null);
    const { t, i18n } = useScreenTranslations('EditOrderBottomSheet');
    const { canChangeBarber, canChangeLocation } = useWidget();

    useImperativeHandle(ref, () => bottomSheetRef.current as BottomSheetRef);

    const currency = cart?.currency || shop?.brand?.currency || 'USD';
    const shopTimezone = shop?.timezone ?? '';
    const shopCoverUrl = shop?.photos?.[0]?.thumbnail || shop?.photos?.[0]?.url;
    // Mirrors the locations picker: the alias leads, because that is what the
    // customer picked from, with the shop name beneath it — the alias carries
    // no location, and the name is the label every other screen shows.
    const shopLabel = shop?.alias || shop?.name || '';
    const shopSecondaryLabel = shop?.name ?? '';
    // With no alias the primary label is already the name — don't repeat it.
    const showShopSecondaryLabel =
      !!shopSecondaryLabel && shopSecondaryLabel !== shopLabel;
    const canEditLocation =
      canChangeLocation && (shop?.brand?.shopCount ?? 0) > 1;

    const appointment = cart?.items?.[0];
    const subItems = appointment?.subItems ?? [];
    const mainService = subItems[0];
    const addons = subItems.slice(1);

    const handleDone = () => {
      bottomSheetRef.current?.close();
      onDone?.();
    };

    const CLOSE_ANIMATION_MS = 300;
    const navigateAway = (href: Parameters<typeof router.push>[0]) => {
      bottomSheetRef.current?.close();
      onCloseOrder?.();
      onDone?.();
      setTimeout(() => router.push(href), CLOSE_ANIMATION_MS);
    };

    const handleEditProfessional = () => {
      if (onEditProfessional) {
        onEditProfessional();
        return;
      }
      if (!shop?.route || !cart?.id) return;
      navigateAway(
        routes.chooseProfessional({ shopRoute: shop.route, cart: cart.id }),
      );
    };

    const handleEditService = () => {
      if (onEditService) {
        onEditService();
        return;
      }
      const barberId = appointment?.bookedWithAnyBarber
        ? 'any'
        : appointment?.barber?.route || appointment?.barberId;
      if (!shop?.route || !cart?.id || !barberId) return;
      navigateAway(
        routes.chooseService({
          shopRoute: shop.route,
          barberId,
          cart: cart.id,
        }),
      );
    };

    const handleEditTime = () => {
      if (onEditTime) {
        onEditTime();
        return;
      }
      bottomSheetRef.current?.close();
      onCloseOrder?.();
      onDone?.();
    };

    const handleEditLocation = () => {
      if (onEditLocation) {
        onEditLocation();
        return;
      }
      if (!shop?.brand?.id) return;
      navigateAway(
        routes.brandLocations({
          brandRoute: shop.brand.id,
          shopId: shop.id,
        }),
      );
    };

    const formatServicePrice = (subItem: CartItem['subItems'][number]) =>
      formatPrice((subItem.cost ?? 0) / 100, {
        locale: i18n.language,
        currency,
      });

    const getBarberName = () => {
      if (appointment?.barber) {
        const { firstName, lastName } = appointment.barber;
        return `${firstName} ${lastName ? `${lastName.at(0)}.` : ''}`.trim();
      }
      return t('guestAppointment');
    };

    const isAnyProfessional =
      !!appointment?.bookedWithAnyBarber && !appointment?.barber;

    const appointmentDate =
      appointment?.dateTime && shopTimezone
        ? parseDateWithTimezone(appointment.dateTime, shopTimezone)
        : null;

    const professionalSubtitle = appointmentDate
      ? formatDate(appointmentDate, DateFormat.CART_ITEM_TIME, i18n.language)
      : '';

    const dateTimeLabel = appointmentDate
      ? formatDate(
          appointmentDate,
          DateFormat.CART_ITEM_DATE_TIME,
          i18n.language,
        )
      : '';

    const badgeDay = appointmentDate
      ? formatDate(appointmentDate, DateFormat.DAY_NUMBER, i18n.language)
      : '';
    const badgeMonth = appointmentDate
      ? formatDate(appointmentDate, DateFormat.MONTH_SHORT, i18n.language)
      : '';

    const totalDuration = subItems.reduce(
      (acc, subItem) => acc + (subItem.duration ?? 0),
      0,
    );

    const renderEditButton = (
      onPress: (() => void) | undefined,
      accessibilityLabel: string,
    ) => (
      <Pressable
        style={styles.editButton}
        onPress={() => onPress?.()}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
      >
        <PencilIcon color="iconOnSelected" size={20} />
      </Pressable>
    );

    return (
      <BottomSheet
        accessibilityLabel={t('title')}
        hideHandle
        hideBackdrop
        testID="edit-order-bottom-sheet"
        ref={bottomSheetRef}
        onDismiss={onDismiss}
        dismissible={false}
        closeOnEscape
        style={styles.bottomSheet}
        containerStyle={styles.bottomSheetContentContainer}
        snapPoints={[{ percentage: '100%' }]}
      >
        <VStack flex style={styles.content}>
          <HStack style={styles.header}>
            <Text variant="2xlStrong" color="textPrimaryInverse">
              {t('title')}
            </Text>
            <Button
              variant="textLink"
              size="small"
              onPress={handleDone}
              accessibilityRole="button"
              accessibilityLabel={t('done')}
            >
              <Text variant="mStrong" color="textPrimaryInverse">
                {t('done')}
              </Text>
            </Button>
          </HStack>

          <ScrollView
            style={styles.scrollView}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.scrollContent}
          >
            <VStack space={theme.spacing[2]}>
              <Text
                variant="s"
                color="textTertiaryInverse"
                style={styles.sectionLabel}
              >
                {t('location')}
              </Text>
              <Card style={styles.card}>
                <View style={styles.row}>
                  <View style={styles.rowContent}>
                    <Avatar
                      shape="square"
                      uri={shopCoverUrl}
                      fallbackText={shopLabel}
                      style={styles.avatarBox}
                    />
                    <VStack flex space={theme.spacing[1]}>
                      <Text
                        variant="lStrong"
                        color="textPrimaryInverse"
                        numberOfLines={1}
                      >
                        {shopLabel}
                      </Text>
                      {showShopSecondaryLabel && (
                        <Text variant="m" color="textSecondaryInverse">
                          {shopSecondaryLabel}
                        </Text>
                      )}
                    </VStack>
                  </View>
                  {canEditLocation &&
                    renderEditButton(
                      handleEditLocation,
                      t('accessibility.editLocation'),
                    )}
                </View>
              </Card>
            </VStack>

            <VStack space={theme.spacing[2]}>
              <Text
                variant="s"
                color="textTertiaryInverse"
                style={styles.sectionLabel}
              >
                {t('professional')}
              </Text>
              <Card style={styles.card}>
                <View style={styles.row}>
                  <View style={styles.rowContent}>
                    {isAnyProfessional ? (
                      <Center style={styles.avatarBox}>
                        <UserIcon color="iconPrimaryInverse" size={24} />
                      </Center>
                    ) : (
                      <Avatar
                        shape="square"
                        uri={appointment?.barber?.photos?.[0]?.url}
                        fallbackText={getBarberName()}
                        style={styles.avatarBox}
                      />
                    )}
                    <VStack flex space={theme.spacing[1]}>
                      <Text
                        variant="lStrong"
                        color="textPrimaryInverse"
                        numberOfLines={1}
                      >
                        {isAnyProfessional
                          ? t('anyProfessional')
                          : getBarberName()}
                      </Text>
                      {!!professionalSubtitle && (
                        <Text variant="m" color="textSecondaryInverse">
                          {professionalSubtitle}
                        </Text>
                      )}
                    </VStack>
                  </View>
                  {canChangeBarber &&
                    renderEditButton(
                      handleEditProfessional,
                      t('accessibility.editProfessional'),
                    )}
                </View>
              </Card>
            </VStack>

            {!!mainService && (
              <VStack space={theme.spacing[2]}>
                <Text
                  variant="s"
                  color="textTertiaryInverse"
                  style={styles.sectionLabel}
                >
                  {t('services')}
                </Text>
                <Card style={styles.card}>
                  <View style={styles.row}>
                    <View style={styles.rowContent}>
                      <Center style={styles.avatarBox}>
                        <ScissorIcon color="iconPrimaryInverse" size={24} />
                      </Center>
                      <VStack flex space={theme.spacing[1]}>
                        <Text
                          variant="lStrong"
                          color="textPrimaryInverse"
                          numberOfLines={1}
                        >
                          {mainService.name}
                        </Text>
                        <Text variant="m" color="textSecondaryInverse">
                          {formatServicePrice(mainService)}
                        </Text>
                      </VStack>
                    </View>
                    {renderEditButton(
                      handleEditService,
                      t('accessibility.editServices'),
                    )}
                  </View>

                  {addons.map((addon) => (
                    <Fragment key={addon.id || `addon-${addon.name}`}>
                      <View style={styles.serviceDivider} />
                      <View style={styles.row}>
                        <View style={styles.rowContent}>
                          <Center style={styles.avatarBox}>
                            <ScissorIcon color="iconPrimaryInverse" size={24} />
                          </Center>
                          <VStack flex space={theme.spacing[1]}>
                            <Text
                              variant="lStrong"
                              color="textPrimaryInverse"
                              numberOfLines={1}
                            >
                              {`+ ${addon.name}`}
                            </Text>
                            <Text variant="m" color="textSecondaryInverse">
                              {formatServicePrice(addon)}
                            </Text>
                          </VStack>
                        </View>
                        <Pressable
                          style={styles.removeButton}
                          onPress={() =>
                            onRemoveAddon?.(addon.referenceId, appointment?.id)
                          }
                          accessibilityRole="button"
                          accessibilityLabel={t('accessibility.removeAddon', {
                            name: addon.name,
                          })}
                        >
                          <TrashIcon color="iconPrimaryInverse" size={20} />
                        </Pressable>
                      </View>
                    </Fragment>
                  ))}
                </Card>
              </VStack>
            )}

            {!!appointmentDate && (
              <VStack space={theme.spacing[2]}>
                <Text
                  variant="s"
                  color="textTertiaryInverse"
                  style={styles.sectionLabel}
                >
                  {t('dateTime')}
                </Text>
                <Card style={styles.card}>
                  <View style={styles.row}>
                    <View style={styles.rowContent}>
                      <Center style={styles.avatarBox}>
                        <Text variant="lStrong" color="textPrimaryInverse">
                          {badgeDay}
                        </Text>
                        <Text
                          variant="s"
                          color="textSecondaryInverse"
                          style={styles.sectionLabel}
                        >
                          {badgeMonth}
                        </Text>
                      </Center>
                      <VStack flex space={theme.spacing[1]}>
                        <Text variant="lStrong" color="textPrimaryInverse">
                          {dateTimeLabel}
                        </Text>
                        <Text variant="m" color="textSecondaryInverse">
                          {formatDuration(totalDuration)}
                        </Text>
                      </VStack>
                    </View>
                    {renderEditButton(
                      handleEditTime,
                      t('accessibility.editTime'),
                    )}
                  </View>
                </Card>
              </VStack>
            )}
          </ScrollView>
        </VStack>
      </BottomSheet>
    );
  },
);

EditOrderBottomSheet.displayName = 'EditOrderBottomSheet';
export { EditOrderBottomSheet };
